import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { type PoolDetail as PoolDetailType } from "@/api/mockData";
import { t, formatRub, type Lang } from "@/lib/i18n";

interface PoolDepositProps {
  pool: PoolDetailType;
  lang: Lang;
  onBack: () => void;
}

export function PoolDeposit({ pool, lang, onBack }: PoolDepositProps) {
  const [amount, setAmount] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [success, setSuccess] = useState(false);

  const remaining = pool.targetAmount - pool.collectedAmount;
  const minDeposit = 5000;

  const presets = [5000, 10000, 25000, 50000];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(true);
  };

  const formatCardInput = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
  };

  const formatExpiryInput = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length >= 3) return digits.slice(0, 2) + "/" + digits.slice(2);
    return digits;
  };

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in space-y-6 text-center px-4">
        <div className="w-20 h-20 rounded-full bg-primary/15 flex items-center justify-center glow-red">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-chart-3">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div>
          <h2 className="font-display text-xl font-bold">{t("deposit.success", lang)}</h2>
          <p className="text-muted-foreground text-sm mt-2">
            {formatRub(Number(amount))} → {pool.name}
          </p>
        </div>
        <Button
          onClick={onBack}
          className="px-8 h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-display font-semibold tracking-wider"
        >
          {t("pool.back", lang)}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors pt-2"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
        </svg>
        {t("pool.back", lang)}
      </button>

      <div>
        <h1 className="font-display text-2xl font-black tracking-tight">{t("deposit.title", lang)}</h1>
        <p className="text-muted-foreground text-sm mt-1.5">{pool.name} — {pool.gpuModel}</p>
      </div>

      {/* Pool Info Strip */}
      <div className="glass-card-featured rounded-2xl p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{t("deposit.remaining", lang)}</p>
            <p className="font-mono-num text-xl font-bold text-primary mt-1">{formatRub(remaining)}</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{t("deposit.min", lang)}</p>
            <p className="font-mono-num text-sm font-bold mt-1">{formatRub(minDeposit)}</p>
          </div>
        </div>
        <div className="h-2.5 bg-muted/40 rounded-full overflow-hidden mt-4">
          <div
            className="progress-bar h-full"
            style={{ width: `${(pool.collectedAmount / pool.targetAmount) * 100}%` }}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Amount */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider">{t("deposit.amount", lang)}</Label>
          <Input
            type="number"
            placeholder="10 000"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-12 font-mono-num text-lg bg-secondary/50 border-border/50 focus:border-primary"
            min={minDeposit}
            max={remaining}
            required
          />
          <div className="flex gap-2">
            {presets.map((val) => (
              <button
                key={val}
                type="button"
                onClick={() => setAmount(String(val))}
                className={`flex-1 py-2.5 rounded-xl text-[11px] font-mono-num font-bold transition-all ${
                  Number(amount) === val
                    ? "bg-primary/15 text-primary border border-primary/30 shadow-[0_0_14px_rgba(220,38,38,0.1)]"
                    : "bg-secondary/30 text-muted-foreground hover:text-foreground border border-white/[0.04]"
                }`}
              >
                {(val / 1000).toFixed(0)}K
              </button>
            ))}
          </div>
        </div>

        {/* Card Details */}
        <Card className="glass-card border-0">
          <CardContent className="p-4 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">{t("deposit.card", lang)}</Label>
              <Input
                type="text"
                placeholder="0000 0000 0000 0000"
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardInput(e.target.value))}
                className="h-11 font-mono-num bg-secondary/50 border-border/50 focus:border-primary tracking-wider"
                maxLength={19}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">{t("deposit.expiry", lang)}</Label>
                <Input
                  type="text"
                  placeholder="MM/YY"
                  value={expiry}
                  onChange={(e) => setExpiry(formatExpiryInput(e.target.value))}
                  className="h-11 font-mono-num bg-secondary/50 border-border/50 focus:border-primary tracking-wider"
                  maxLength={5}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">{t("deposit.cvv", lang)}</Label>
                <Input
                  type="password"
                  placeholder="•••"
                  value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 3))}
                  className="h-11 font-mono-num bg-secondary/50 border-border/50 focus:border-primary tracking-wider text-center"
                  maxLength={3}
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <button
          type="submit"
          disabled={!amount || !cardNumber || !expiry || !cvv}
          className="w-full h-[56px] rounded-2xl cta-primary font-display flex items-center justify-center gap-2.5 disabled:opacity-30 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
          </svg>
          {t("deposit.pay", lang)} {amount ? formatRub(Number(amount)) : ""}
        </button>
      </form>
    </div>
  );
}
